import { useLazyQuery } from "@apollo/client";
import { useAuth } from "@src/common/hooks/useAuth";
import { useErrorToast } from "@src/common/hooks/useErrorToast";
import { useEffect } from "react";
import {
  GET_SHIPMENT_ORDER_BILLABLE_ITEM,
  GET_TEMPLATE_ORDER_BILLABLE_ITEM,
} from "../graphql/orderBillableItem";
import {
  BillableItemSummary,
  OrderBillableItemState,
} from "../types/billableItemAssignment";

interface OrderWithBillableItem {
  id: string;
  endCustomer?: { id: string } | null;
  billableItem?: BillableItemSummary | null;
}

interface ShipmentOrderBillableItemData {
  shipmentById?: { id: string; order?: OrderWithBillableItem | null } | null;
}

interface TemplateOrderBillableItemData {
  shipmentTemplateById?: {
    id: string;
    order?: OrderWithBillableItem | null;
  } | null;
}

interface UseOrderBillableItemArgs {
  shipmentId?: string;
  shipmentTemplateId?: string;
}

/**
 * Loads the billable item assigned to the selected row's order.
 *
 * Live orders and recurring templates are read through different query roots, so the
 * caller passes whichever id its page has. Deliberately not part of the table queries:
 * GetShipments polls every 10 seconds and shares its selection set with the
 * sales-report variant.
 */
export function useOrderBillableItem({
  shipmentId,
  shipmentTemplateId,
}: UseOrderBillableItemArgs): OrderBillableItemState {
  const { courierId } = useAuth();
  const showError = useErrorToast();

  const [fetchShipmentOrder, shipmentOrderResult] =
    useLazyQuery<ShipmentOrderBillableItemData>(
      GET_SHIPMENT_ORDER_BILLABLE_ITEM,
      {
        fetchPolicy: "cache-and-network",
        onError: (error) => showError(error),
      }
    );
  const [fetchTemplateOrder, templateOrderResult] =
    useLazyQuery<TemplateOrderBillableItemData>(
      GET_TEMPLATE_ORDER_BILLABLE_ITEM,
      {
        fetchPolicy: "cache-and-network",
        onError: (error) => showError(error),
      }
    );

  useEffect(() => {
    if (!courierId) return;
    if (shipmentId) {
      fetchShipmentOrder({ variables: { courierId, shipmentId } });
      return;
    }
    if (shipmentTemplateId) {
      fetchTemplateOrder({ variables: { courierId, shipmentTemplateId } });
    }
  }, [courierId, shipmentId, shipmentTemplateId]);

  /**
   * `cache-and-network` can keep returning the previous variables' `data` while a new
   * request for a different id is in flight, so only trust a result whose entity id
   * matches what was actually requested for the current render.
   */
  function getOrderForRequestedId(): OrderWithBillableItem | null {
    if (shipmentId) {
      const matchedShipment = shipmentOrderResult.data?.shipmentById;
      if (matchedShipment?.id !== shipmentId) return null;
      return matchedShipment.order ?? null;
    }
    if (shipmentTemplateId) {
      const matchedTemplate = templateOrderResult.data?.shipmentTemplateById;
      if (matchedTemplate?.id !== shipmentTemplateId) return null;
      return matchedTemplate.order ?? null;
    }
    return null;
  }

  const order: OrderWithBillableItem | null = getOrderForRequestedId();

  function refetch() {
    if (!courierId) return;
    if (shipmentId) {
      fetchShipmentOrder({
        variables: { courierId, shipmentId },
        fetchPolicy: "network-only",
      });
      return;
    }
    if (shipmentTemplateId) {
      fetchTemplateOrder({
        variables: { courierId, shipmentTemplateId },
        fetchPolicy: "network-only",
      });
    }
  }

  return {
    orderId: order?.id ?? null,
    endCustomerId: order?.endCustomer?.id ?? null,
    billableItem: order?.billableItem ?? null,
    loading: shipmentId
      ? shipmentOrderResult.loading
      : templateOrderResult.loading,
    refetch,
  };
}
